import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { VENDOR_LOGOS, type VendorBrand } from './vendor-logos.ts';

/**
 * `<chassis-feature-shape>` — non-interactive chassis features.
 *
 * Renders the small physical features that anchor a panel face but
 * are not controls: mounting screws, card slots, engraved legends,
 * vendor name plates. The `shape` attribute selects the visual
 * idiom; the element is intentionally minimal because these features
 * are part of the panel's authenticity, not its interactive surface.
 */
@customElement('chassis-feature-shape')
export class ChassisFeatureShapeElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        inline-size: 100%;
        block-size: 100%;
        pointer-events: none;
        /* Establish a size-container so legend text-sizing can resolve
         * against the host's box height (constant across all labels)
         * rather than against the SVG viewBox scaling (which differs per
         * label width and makes narrow boxes render text smaller). */
        container-type: size;
        /* Canonical silkscreen-ink colour. The same off-white pigment
         * shared across the panel's printed surfaces: engraved-text
         * legends (.legend), vendor wordmarks (.logo) and push-button
         * cap labels (--pb-label-chassis on <push-button>). One ink,
         * one tone — the broadcast-engineer convention. */
        --silkscreen-colour: #e8e6e2;
      }

      svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        overflow: visible;
      }

      /* Broadcast-rack mounting hardware: a black-phosphate Phillips
       * pan-head screw seated in a matte-black silicone bumper. The
       * bumper protects the rack rails from the chassis face and
       * dampens vibration — standard fare on Grass Valley and Sony
       * panels alike. Rendered as three concentric layers so the
       * recessed screw reads with subtle depth even at small sizes. */
      .screw-bumper {
        /* Soft outer rubber rim — slightly lighter than the chassis
         * so the bumper reads as a discrete component. */
        fill: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 55%, #000);
        stroke: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 30%, #000);
      }
      .screw-bumper-recess {
        /* Inner well inside the bumper — darker, drops toward the
         * screw head and hides the rubber-to-metal transition. */
        fill: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 15%, #000);
        stroke: none;
      }
      .screw {
        /* Phosphate-black screw head. No specular hint — broadcast
         * hardware is deliberately matte to keep stage lights off it. */
        fill: #050507;
        stroke: #1a1a1d;
      }
      .screw-slot {
        /* Cross-recess interior. A touch lighter than the head so the
         * Phillips slots read at thumbnail scale. */
        stroke: #2a2a2d;
        stroke-linecap: round;
      }
      .slot {
        fill: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 30%, black);
        stroke: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 50%, black);
      }
      .section {
        /* Chassis tonal break — a flat tinted rectangle drawn behind
         * every other control. The OCP-400's physical chassis is mostly
         * the darker base tone; the two RECESSES (joystick well and
         * LCD-menu recess) are only marginally darker, while the
         * raised PLATES that mount the button rows are noticeably
         * lighter than the base. The default '.section' style models
         * the joystick recess: just a touch darker than the base. */
        fill: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 88%, black);
        stroke: none;
      }

      :host([variant='section-deep']) .section {
        /* LCD menu-recess — the deepest recess on the chassis face.
         * Slightly more darker than the joystick recess, but still
         * subtle enough that it reads as "the same base, recessed"
         * rather than a hard-edged tonal field. */
        fill: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 78%, black);
      }

      /* Joystick lip — three-layer chassis recess traced from the
       * user's Illustrator file (research/sources/layout/ocp-400-
       * dike.svg). The SVG defines three nested rounded rectangles
       * representing the physical surfaces of the milled chassis
       * well, in three deterministic chassis-tone shades:
       *
       *   .dike-rim      outer raised lip rim  (lighter than chassis)
       *   .dike-floor    recess floor          (medium)
       *   .dike-slot     inner shaft slot      (darkest, deepest)
       *
       * The "venusberg" reading — slightly raised rim, depressed
       * centre, deep slot at the core — is the intentional ergonomic
       * 3D cue. Each layer carries a subtle vertical gradient so
       * the recess catches simulated ambient light from above. */
      :host([variant='section-recess']) .dike-rim {
        fill: url(#cf-dike-rim-grad);
      }
      :host([variant='section-recess']) .dike-floor {
        fill: url(#cf-dike-floor-grad);
      }
      :host([variant='section-recess']) .dike-slot {
        fill: url(#cf-dike-slot-grad);
      }

      :host([variant='section-plate']) .section {
        /* Raised mounting plate — semi-transparent white overlay that
         * lets the chassis-frame's underlying vertical gradient show
         * through, so the plate inherits the panel's 3D metal-finish
         * shading naturally (top a hair brighter, bottom a hair darker)
         * rather than reading as a flat coloured rectangle. 8 % opacity
         * is the sweet spot — clearly visible as a raised surface above
         * the darker chassis base, but not so opaque that the chassis
         * gradient disappears. */
        fill: white;
        fill-opacity: 0.08;
      }
      .legend {
        font-family: var(--font-ui, system-ui);
        /* Size against host-height so the legend's cap-height scales
         * 1:1 with the bbox's vbu height across all panel render
         * scales. 70cqh resolves to 70% of the host's CSS-pixel
         * height; cap-height is then ~0.49 × bbox-h (in vbu),
         * independent of how zoomed the panel is. The lower bound
         * (0.5rem) keeps the smallest labels legible at extreme
         * zoom-out; there is deliberately no upper clamp — a
         * 1rem upper clamp (the earlier formula) made labels
         * shrink proportionally at high zoom because the clamp
         * limited the CSS-pixel font-size below what 70cqh would
         * have produced, breaking the half-size relationship
         * between, e.g., the model label and the vendor wordmark. */
        font-size: max(0.5rem, 70cqh);
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        /* Silkscreen ink — real screen-printed pigment on a matte
         * broadcast chassis. Sits in the same ink family as the
         * vendor wordmarks (.logo) and the push-button cap labels
         * (--pb-label-chassis): a slightly warm off-white that
         * reads as printed ink, not paper white. The shared
         * --silkscreen-colour custom property keeps every silkscreen
         * surface on the panel visually identical, which mirrors the
         * physical reality (one ink batch across the whole chassis). */
        fill: var(--legend-colour, var(--silkscreen-colour));
        /* text-anchor is set per-element via the legend-align
         * property so left/middle/right anchoring can be selected
         * per chassis-feature instance. dominant-baseline stays
         * here because vertical centring is uniform across all
         * legends. */
        dominant-baseline: middle;
      }

      :host([variant='amber']) {
        --legend-colour: #d8941a;
      }

      /* Stroke-only bracket outline used to frame labelled control
       * groups on Sony panels (WHITE rotaries, BLACK/FLARE rotaries,
       * AUTO SETUP cluster). The bracket reads as a moulded
       * silkscreen line — a thin pale-grey stroke just inside the
       * group's bounding box, no fill. */
      .bracket {
        fill: none;
        stroke: color-mix(in oklab, var(--chassis-colour, #1a1a1a) 40%, white);
        stroke-width: 0.6;
        opacity: 0.5;
      }

      /* Vendor wordmark silkscreen. Real screen-printed ink on a
       * matte broadcast chassis never reads as paper-pure #fff —
       * the pigment plus the matte topcoat gives a slightly warm
       * off-white. The mark reads in the canonical silkscreen
       * tone (--silkscreen-colour) so it sits in the same ink
       * family as the engraved-text legends and the push-button
       * cap labels. The wrapper sets the colour and the inlined
       * SVG glyph (which paints with fill=currentColor) inherits
       * it. The wordmark is centred and aspect-preserved inside
       * the inventory bbox so the asset's intrinsic ratio is
       * respected regardless of the exact box dimensions the
       * panel author specifies. */
      .logo {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        color: var(--silkscreen-colour);
      }
      .logo > svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
      }
    `,
  ];

  /** Visual idiom selector. */
  @property({ type: String, reflect: true }) accessor shape:
    | 'screw'
    | 'slot'
    | 'section'
    | 'legend'
    | 'bracket'
    | 'logo' = 'screw';

  /** Legend text when `shape="legend"`. */
  @property({ type: String }) accessor label = '';

  /**
   * Vendor wordmark to render when `shape="logo"`. The brand string
   * keys into `VENDOR_LOGOS`; an unknown brand falls back to an empty
   * render so a typo never surfaces an unbranded panel as if it had
   * a logo at all. Defaults to `sony` because Sony is the most
   * common vendor across the current pedestal-panels fleet.
   */
  @property({ type: String, reflect: true }) accessor brand: VendorBrand = 'sony';

  /**
   * Horizontal anchor for `shape="legend"` text.
   *
   *   - `middle` (default) — text-anchor middle, centred in bbox.
   *   - `start` — text-anchor start, flush to bbox left edge.
   *   - `end` — text-anchor end, flush to bbox right edge.
   *
   * The legend SVG's `preserveAspectRatio` is matched to the anchor
   * (xMin/xMid/xMax) so the viewBox's left/middle/right column maps
   * to the bbox's actual left/middle/right edge — without that, a
   * height-constrained viewBox would centre horizontally inside the
   * bbox and 'end' would land inside the bbox rather than at its
   * right edge.
   */
  @property({ type: String, reflect: true, attribute: 'legend-align' }) accessor legendAlign:
    | 'start'
    | 'middle'
    | 'end' = 'middle';

  /**
   * Optional variant tag.
   *
   *   - `engraved` (default) — standard engraved chassis features (screws,
   *     slots, plain sections; renders a subtly recessed tone for sections).
   *   - `amber` — amber-engraved chassis labels (GAIN / BLACK / SKIN /
   *     FLARE / GAMMA on the OCP-400).
   *   - `section-deep` — the deepest recess, for the OCP-400 LCD
   *     menu-recess area (marginally more recessed than the joystick well).
   *   - `section-recess` — the joystick-lip recess, same tonal depth as
   *     the default section but with markedly rounded corners (rx=18)
   *     matching the physical milled feature in the OCP-400's lower
   *     chassis that frames the monoknob assembly.
   *   - `section-plate` — a raised mounting plate, LIGHTER than the
   *     chassis base. Used for the OCP-400's upper and lower button-row
   *     plates.
   */
  @property({ type: String, reflect: true }) accessor variant:
    | 'engraved'
    | 'amber'
    | 'section-deep'
    | 'section-recess'
    | 'section-plate' = 'engraved';

  override render() {
    switch (this.shape) {
      case 'slot':
        return html`
          <svg viewBox="0 0 100 20" preserveAspectRatio="none">
            <rect class="slot" x="2" y="3" width="96" height="14" rx="2" stroke-width="0.6" />
          </svg>
        `;
      case 'section':
        if (this.variant === 'section-recess') {
          // Joystick lip — three-layer chassis recess. Coordinates
          // taken directly from research/sources/layout/ocp-400-
          // dike.svg (Adobe Illustrator user-authored file). The
          // viewBox matches the SVG file's viewBox so the geometry
          // is bit-for-bit the artwork the user drew, then scaled
          // by the host element's bounding box.
          return html`
            <svg viewBox="0 0 73.701 119.055" preserveAspectRatio="none">
              <defs>
                <!-- Outer raised rim — picks up simulated ambient light
                     from above so the rim reads as a chassis edge
                     standing slightly proud of the recess floor. -->
                <linearGradient id="cf-dike-rim-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 35%, #b0b0af)" />
                  <stop offset="0.15" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 30%, #a8a8a7)" />
                  <stop offset="0.85" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 35%, #9c9c9b)" />
                  <stop offset="1" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 50%, #888887)" />
                </linearGradient>
                <!-- Recess floor — medium tone, shaped as a 3D bulge.
                     The floor reads physically as a half-cylindrical
                     hump running across the dike, peaking at the
                     vertical mid-point and sloping back down toward
                     the chassis base level at both the top and bottom
                     edges. Implemented with a symmetric vertical
                     gradient: deep shadow at the strip edges (base
                     level), a bright crest at offset 0.5 (the highest
                     point of the hump). The bottom edge is a hair
                     lighter than the top — perspective bounce light
                     from the operator's near side. -->
                <linearGradient id="cf-dike-floor-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 80%, #2a2a29)" />
                  <stop offset="0.22" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 55%, #5a5a59)" />
                  <stop offset="0.5" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 30%, #909090)" />
                  <stop offset="0.78" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 55%, #5a5a59)" />
                  <stop offset="1" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 72%, #3a3a39)" />
                </linearGradient>
                <!-- Inner slot — deepest, near-black. A thin highlight
                     band at the very top reads as the slot's upper lip
                     catching light; the body is uniformly dark. -->
                <linearGradient id="cf-dike-slot-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 60%, #444443)" />
                  <stop offset="0.03" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 88%, #050505)" />
                  <stop offset="0.12" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 92%, #050505)" />
                  <stop offset="0.88" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 92%, #050505)" />
                  <stop offset="0.97" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 80%, #050505)" />
                  <stop offset="1" stop-color="color-mix(in oklab, var(--chassis-colour, #1a1a1a) 72%, #2a2a29)" />
                </linearGradient>
              </defs>
              <!-- Layer 1: outer raised rim. -->
              <rect class="dike-rim" x="4.406" y="3.583" width="64.889" height="111.344" rx="2.901" ry="2.901" />
              <!-- Layer 2: recess floor, inset inside the rim. -->
              <rect class="dike-floor" x="7.855" y="6.367" width="57.991" height="106.322" rx="2.772" ry="2.772" />
              <!-- Layer 3: inner shaft slot, the deepest and darkest
                   element. Where the joystick shaft passes through
                   the chassis on the real hardware. -->
              <rect class="dike-slot" x="22.084" y="6.506" width="29.533" height="105.497" rx="3.091" ry="3.091" />
            </svg>
          `;
        }
        return html`
          <svg preserveAspectRatio="none">
            <rect class="section" x="0" y="0" width="100%" height="100%" rx="3" ry="3" />
          </svg>
        `;
      case 'legend': {
        // Map legend-align to matching SVG attributes so the text
        // anchors at the bbox's actual left / middle / right edge.
        // The preserveAspectRatio must follow the anchor; otherwise
        // a height-constrained viewBox centres horizontally inside
        // the bbox and 'end' would land short of the bbox's right
        // edge by half the unused horizontal slack.
        const anchor =
          this.legendAlign === 'start'
            ? 'start'
            : this.legendAlign === 'end'
              ? 'end'
              : 'middle';
        const textX = anchor === 'start' ? 0 : anchor === 'end' ? 100 : 50;
        const par =
          anchor === 'start'
            ? 'xMinYMid meet'
            : anchor === 'end'
              ? 'xMaxYMid meet'
              : 'xMidYMid meet';
        return html`
          <svg viewBox="0 0 100 30" preserveAspectRatio=${par}>
            <text class="legend" text-anchor=${anchor} x=${textX} y="15">${this.label}</text>
          </svg>
        `;
      }
      case 'bracket':
        // Outlined rectangle used to frame a labelled control
        // group on Sony panels. Rounded corners (rx=2) match the
        // SVG silkscreen path style; preserveAspectRatio="none"
        // stretches the bracket to fill its inventory-defined bbox
        // exactly.
        return html`
          <svg preserveAspectRatio="none" viewBox="0 0 100 100">
            <rect class="bracket"
              x="0.5" y="0.5"
              width="99" height="99"
              rx="2.5" ry="2.5"
            />
          </svg>
        `;
      case 'logo': {
        // Vendor wordmark silkscreen — Sony on Sony panels,
        // Grass Valley on GV panels. The inlined SVG carries its
        // own viewBox and `preserveAspectRatio="xMidYMid meet"`
        // so the wordmark fits-and-centres inside the bbox the
        // inventory entry defines, without distortion. Shadow
        // DOM isolates the SVG's internal IDs from the document,
        // so the GV mark's `<clipPath id="a">` etc. cannot
        // collide with any other element.
        const markup = VENDOR_LOGOS[this.brand];
        return html`<div class="logo" role="img">${unsafeSVG(markup ?? '')}</div>`;
      }
      case 'screw':
      default:
        return html`
          <svg viewBox="0 0 20 20">
            <!-- Rubber bumper outer ring -->
            <circle class="screw-bumper" cx="10" cy="10" r="9.5" stroke-width="0.3" />
            <!-- Inner bumper recess — the well the screw sits in -->
            <circle class="screw-bumper-recess" cx="10" cy="10" r="7.6" />
            <!-- Phosphate-black Phillips pan-head -->
            <circle class="screw" cx="10" cy="10" r="6.4" stroke-width="0.3" />
            <!-- Phillips cross — two perpendicular slots, each ~60 % of
                 the head diameter, slightly shorter than a full slotted
                 screw to read as a cross-recess rather than two slots. -->
            <line
              class="screw-slot"
              x1="6.2"
              y1="10"
              x2="13.8"
              y2="10"
              stroke-width="1.2"
            />
            <line
              class="screw-slot"
              x1="10"
              y1="6.2"
              x2="10"
              y2="13.8"
              stroke-width="1.2"
            />
          </svg>
        `;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chassis-feature-shape': ChassisFeatureShapeElement;
  }
}
